import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Stack, Card, CardContent, CardActions,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, Alert, CircularProgress,
  FormControlLabel, Switch, InputAdornment,
} from '@mui/material';
import {
  Add, Edit, Delete, FitnessCenter, Lock, LockOpen, Close,
  YouTube, VideoFile, Image as ImageIcon, DeleteOutline, Search, Clear,
  AutoAwesome, Refresh,
} from '@mui/icons-material';
import { exercisesAPI, uploadAPI } from '../services/api';
import { SERVER_URL } from '../services/api';
import PaginationBar from '../components/PaginationBar';
import PageHeader from '../components/PageHeader';

const MUSCLE_GROUPS = [
  'Abs', 'Back', 'Biceps', 'Calves', 'Cardio',
  'Chest', 'Core', 'Forearms', 'Hips', 'Shoulders',
  'Trapezius', 'Triceps',
];

const ACTIVITY_TYPES = ['gym', 'yoga', 'hiit', 'home_training', 'mindfulness'];

const emptyExercise = () => ({
  name: '',
  description: '',
  muscleGroup: '',
  difficulty: 'beginner',
  equipment: [],
  instructions: [],
  activityType: 'gym',
  durationSeconds: '',
  secondsPerRep: 2,
  breathCount: '',
  coachingCue: '',
  imageUrl: '',
  videoUrl: '',
  videoUrlMale: '',
  videoUrlFemale: '',
  youtubeLink: '',
  isGlobal: true,
  caloriesBurnedPerRep: '',
});

function getStatusLabel(exercise) {
  if (exercise.isPublished) return 'Published';
  if (exercise.pendingReview) return 'Pending Review';
  return 'Draft';
}

function getStatusColor(exercise) {
  if (exercise.isPublished) return 'success';
  if (exercise.pendingReview) return 'warning';
  return 'default';
}

function getCurrentUser() {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
}

export default function Exercises() {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterActivity, setFilterActivity] = useState('');

  const user = getCurrentUser();
  const isApprover = ['admin', 'superadmin'].includes(user?.role);
  const isTrainer = user?.role === 'trainer';

  // Debounce: wait 400ms after the user stops typing before updating debouncedSearch
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 whenever the debounced search term changes
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const fetchExercises = useCallback(async () => {
    try {
      setLoading(true);
      const skip = (page - 1) * limit;
      const params = {
        limit,
        skip,
        ...(isApprover || isTrainer ? { admin: 'true' } : {}),
        ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
        ...(filterActivity ? { activityType: filterActivity } : {}),
      };
      const res = await exercisesAPI.getAll(params);
      setExercises(res.data.data || []);
      setTotal(res.data.total || 0);
      setPages(Math.ceil((res.data.total || 0) / limit));
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load exercises');
    } finally {
      setLoading(false);
    }
  }, [isApprover, isTrainer, page, limit, debouncedSearch, filterActivity]);

  useEffect(() => { fetchExercises(); }, [fetchExercises]);

  const handleSearch = (value) => {
    setSearch(value);
  };

  const handleSave = async (data) => {
    try {
      if (data._id) {
        await exercisesAPI.update(data._id, data);
        setSuccess('Exercise updated');
      } else {
        await exercisesAPI.create(data);
        setSuccess('Exercise created');
      }
      setFormOpen(false);
      setEditingExercise(null);
      fetchExercises();
    } catch (e) {
      throw e;
    }
  };

  const handleDelete = async () => {
    try {
      await exercisesAPI.delete(deleteId);
      setSuccess('Exercise deleted');
      setDeleteId(null);
      fetchExercises();
    } catch (e) {
      setError('Failed to delete exercise');
    }
  };

  const togglePublish = async (exercise) => {
    try {
      await exercisesAPI.update(exercise._id, { isPublished: !exercise.isPublished });
      fetchExercises();
    } catch (e) {
      setError('Failed to update publish state');
    }
  };

  const openAdd = () => {
    setEditingExercise(emptyExercise());
    setFormOpen(true);
  };

  const openEdit = (exercise) => {
    setEditingExercise({
      ...emptyExercise(),
      ...exercise,
      equipment: exercise.equipment || [],
      instructions: exercise.instructions || [],
      durationSeconds: exercise.durationSeconds || '',
      secondsPerRep: exercise.secondsPerRep || 2,
      breathCount: exercise.breathCount || '',
      coachingCue: exercise.coachingCue || '',
      caloriesBurnedPerRep: exercise.caloriesBurnedPerRep || '',
      videoUrlMale: exercise.videoUrlMale || '',
      videoUrlFemale: exercise.videoUrlFemale || '',
    });
    setFormOpen(true);
  };

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Exercises"
        subtitle="Manage exercise library and upload videos."
        gradient="linear-gradient(90deg, #0891B2, #22D3EE)"
        actions={<>
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={fetchExercises}>Refresh</Button>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={openAdd}>
            Add Exercise
          </Button>
        </>}
      />

      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search exercises by name..."
        value={search}
        onChange={e => handleSearch(e.target.value)}
        sx={{ mb: 1.5 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => handleSearch('')}>
                <Clear fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {/* Activity type filter chips */}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
        {['', ...ACTIVITY_TYPES].map(t => (
          <Chip
            key={t || 'all'}
            label={t ? t.replace('_', ' ') : 'All Types'}
            size="small"
            onClick={() => { setFilterActivity(t); setPage(1); }}
            sx={{
              textTransform: 'capitalize',
              cursor: 'pointer',
              fontWeight: 700,
              px: 1.5,
              py: 0.5,
              ...(filterActivity === t
                ? { bgcolor: '#2563EB', color: '#FFFFFF', '&:hover': { bgcolor: '#1D4ED8' } }
                : { bgcolor: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB', '&:hover': { bgcolor: '#E5E7EB' } }),
            }}
          />
        ))}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : exercises.length === 0 ? (
        <Box textAlign="center" py={8}>
          <FitnessCenter sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="body1" fontWeight={600} gutterBottom>No exercises yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Add your first exercise to the library.</Typography>
          <Button variant="contained" startIcon={<Add />} sx={{ mt: 1 }} onClick={openAdd}>
            Add First Exercise
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {exercises.map(exercise => (
            <Grid item xs={12} md={6} lg={4} key={exercise._id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%', display: 'flex', flexDirection: 'column',
                  border: 1, borderColor: 'divider', borderRadius: 2,
                  transition: 'border-color 0.15s',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                {exercise.imageUrl && (
                  <Box
                    component="img"
                    src={exercise.imageUrl}
                    alt={exercise.name}
                    sx={{ width: '100%', height: 140, objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="body1" fontWeight={600} sx={{ flexGrow: 1, mr: 1, fontSize: '0.9375rem' }}>
                      {exercise.name}
                    </Typography>
                    <Chip
                      label={getStatusLabel(exercise)}
                      color={getStatusColor(exercise)}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 22,
                        ...(exercise.isPublished && {
                          bgcolor: '#DCFCE7', color: '#15803D', border: '1px solid #86EFAC',
                        }),
                        ...(exercise.pendingReview && {
                          bgcolor: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A',
                        }),
                        ...(!exercise.isPublished && !exercise.pendingReview && {
                          bgcolor: '#F3F4F6', color: '#374151', border: '1px solid #D1D5DB',
                        }),
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" mb={1.5} sx={{ minHeight: 36, fontSize: '0.8125rem' }}>
                    {exercise.description || 'No description'}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                    <Chip label={exercise.muscleGroup} size="small" sx={{ fontWeight: 600, fontSize: '0.6875rem', bgcolor: '#F3F4F6', color: '#1F2937', border: '1px solid #E5E7EB' }} />
                    <Chip label={exercise.difficulty} size="small" sx={{ fontWeight: 600, fontSize: '0.6875rem', bgcolor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }} />
                    <Chip label={exercise.activityType} size="small" sx={{ fontWeight: 600, fontSize: '0.6875rem', bgcolor: '#F5F3FF', color: '#6D28D9', border: '1px solid #DDD6FE' }} />
                  </Stack>
                  {(exercise.videoUrl || exercise.videoUrlMale || exercise.youtubeLink) && (
                    <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
                      {exercise.youtubeLink
                        ? <><YouTube fontSize="small" color="error" /><Typography variant="caption" color="text.secondary">YouTube</Typography></>
                        : <><VideoFile fontSize="small" color="primary" /><Typography variant="caption" color="text.secondary">Video attached</Typography></>
                      }
                    </Stack>
                  )}
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Button size="small" startIcon={<Edit />} onClick={() => openEdit(exercise)}>
                    Edit
                  </Button>
                  {isApprover && (
                    <Button
                      size="small"
                      startIcon={exercise.isPublished ? <LockOpen /> : <Lock />}
                      onClick={() => togglePublish(exercise)}
                    >
                      {exercise.isPublished ? 'Unpublish' : 'Publish'}
                    </Button>
                  )}
                  {isTrainer && !exercise.isPublished && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={exercise.pendingReview}
                      onClick={() => {/* submit for review */ }}
                    >
                      {exercise.pendingReview ? 'Pending Review' : 'Submit for Review'}
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    sx={{ ml: 'auto' }}
                    onClick={() => setDeleteId(exercise._id)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <PaginationBar page={page} pages={pages} total={total} limit={limit} onPage={setPage} onLimit={v => { setLimit(v); setPage(1); }} />

      {formOpen && editingExercise && (
        <ExerciseFormDialog
          exercise={editingExercise}
          isApprover={isApprover}
          isTrainer={isTrainer}
          onSave={handleSave}
          onClose={() => { setFormOpen(false); setEditingExercise(null); }}
          onError={setError}
        />
      )}

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Exercise?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete the exercise.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}



// ————————————————————————————————————————————————————————————————————————————————
function ExerciseFormDialog({ exercise: initialExercise, isApprover, isTrainer, onSave, onClose, onError }) {
  const [exercise, setExercise] = useState({ ...initialExercise });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const user = getCurrentUser();
  const canUseAI = user?.role === 'superadmin';
  const [imageUploading, setImageUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [mediaMode, setMediaMode] = useState(() => {
    if (initialExercise?.youtubeLink?.trim()) return 'youtube';
    if (
      initialExercise?.videoUrlMale?.trim() ||
      initialExercise?.videoUrlFemale?.trim() ||
      initialExercise?.videoUrl?.trim()
    ) {
      return 'upload';
    }
    return 'none';
  });

  // Pending video files (not yet uploaded â€” uploaded on Save)
  const [pendingMaleVideo, setPendingMaleVideo] = useState(null);
  const [pendingFemaleVideo, setPendingFemaleVideo] = useState(null);

  const update = (field, value) => setExercise(prev => ({ ...prev, [field]: value }));

  const handleGenerateAI = async () => {
    if (!canUseAI) {
      setError('Only super admin can use AI exercise generation.');
      return;
    }

    const name = exercise.name?.trim();
    if (!name) {
      setError('Enter an exercise name first, then use AI to fill the details.');
      return;
    }

    setError('');
    setAiGenerating(true);
    try {
      const res = await exercisesAPI.generateAI({ name });
      const generated = res.data.data || {};
      setExercise(prev => ({
        ...prev,
        ...generated,
        name: generated.name || prev.name,
        imageUrl: prev.imageUrl || generated.imageUrl || '',
        videoUrl: prev.videoUrl || '',
        videoUrlMale: prev.videoUrlMale || '',
        videoUrlFemale: prev.videoUrlFemale || '',
        youtubeLink: prev.youtubeLink || '',
        isGlobal: prev.isGlobal,
        isPublished: prev.isPublished,
        pendingReview: prev.pendingReview,
      }));
    } catch (e) {
      const msg = e.response?.data?.message || 'AI could not generate exercise details. Please try again.';
      setError(msg);
      onError(msg);
    } finally {
      setAiGenerating(false);
    }
  };

  const handleMediaModeChange = (mode) => {
    setMediaMode(mode);
    if (mode === 'youtube') {
      update('videoUrlMale', '');
      update('videoUrlFemale', '');
      update('videoUrl', '');
      setPendingMaleVideo(null);
      setPendingFemaleVideo(null);
    } else if (mode === 'upload') {
      update('youtubeLink', '');
    } else {
      update('videoUrlMale', '');
      update('videoUrlFemale', '');
      update('videoUrl', '');
      update('youtubeLink', '');
      setPendingMaleVideo(null);
      setPendingFemaleVideo(null);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setImageUploading(true);
      const res = await uploadAPI.upload(file);
      const url = res.data.data?.url || res.data.url;
      // Firebase Storage returns absolute URLs; legacy local disk returns relative paths
      const fullUrl = url.startsWith('http') ? url : `${SERVER_URL}${url}`;
      update('imageUrl', fullUrl);
    } catch {
      setError('Image upload failed');
    } finally {
      setImageUploading(false);
    }
  };

  const handleSave = async () => {
    if (!exercise.name?.trim()) { setError('Exercise name is required'); return; }
    if (!exercise.muscleGroup) { setError('Muscle group is required'); return; }
    if (!exercise.description?.trim()) { setError('Description is required'); return; }

    if (exercise.youtubeLink && !exercise.youtubeLink.match(/^https:\/\/(www\.youtube\.com|youtu\.be)\//)) {
      setError('YouTube link must start with https://www.youtube.com/ or https://youtu.be/');
      return;
    }

    setSaving(true);
    try {
      // Upload videos first if pending
      if (pendingMaleVideo || pendingFemaleVideo) {
        setVideoUploading(true);
        const res = await uploadAPI.uploadExerciseVideos({
          maleVideo: pendingMaleVideo || undefined,
          femaleVideo: pendingFemaleVideo || undefined,
          gymName: exercise.gymName || 'global',
          muscleGroup: exercise.muscleGroup,
        });
        setVideoUploading(false);
        const { videoUrlMale, videoUrlFemale } = res.data.data;
        // Firebase Storage returns absolute URLs; legacy returns relative paths
        const toAbs = (url) => (!url ? null : url.startsWith('http') ? url : `${SERVER_URL}${url}`);
        if (videoUrlMale) exercise.videoUrlMale = toAbs(videoUrlMale);
        // Female falls back to male if not provided
        if (videoUrlFemale) exercise.videoUrlFemale = toAbs(videoUrlFemale);
        else if (videoUrlMale && !exercise.videoUrlFemale) exercise.videoUrlFemale = '';
      }

      const payload = {
        ...exercise,
        equipment: typeof exercise.equipment === 'string'
          ? exercise.equipment.split(',').map(s => s.trim()).filter(Boolean)
          : exercise.equipment || [],
        instructions: typeof exercise.instructions === 'string'
          ? exercise.instructions.split('\n').map(s => s.trim()).filter(Boolean)
          : exercise.instructions || [],
        durationSeconds: exercise.durationSeconds ? Number(exercise.durationSeconds) : undefined,
        secondsPerRep: exercise.secondsPerRep ? Number(exercise.secondsPerRep) : 2,
        caloriesBurnedPerRep: exercise.caloriesBurnedPerRep ? Number(exercise.caloriesBurnedPerRep) : undefined,
      };

      await onSave(payload);
    } catch (e) {
      const msg = e.response?.data?.message || 'Failed to save exercise';
      setError(msg);
      onError(msg);
    } finally {
      setSaving(false);
      setVideoUploading(false);
    }
  };

  const equipmentValue = Array.isArray(exercise.equipment) ? exercise.equipment.join(', ') : exercise.equipment || '';
  const instructionsValue = Array.isArray(exercise.instructions) ? exercise.instructions.join('\n') : exercise.instructions || '';

  // Effective video URLs for preview — prepend SERVER_URL if path is relative
  const toAbsolute = (url) => {
    if (!url) return '';
    let clean = String(url).replace(/\\/g, '/');
    if (clean.includes('fitistic.fitistic.app/api/media/')) {
      clean = clean.replace(/^https?:\/\/fitistic\.yuviso\.com\/api\/media\//, '/api/media/');
    }
    if (clean.includes('api-gym.yuviso.com/api/media/')) {
      clean = clean.replace(/^https?:\/\/api-gym\.yuviso\.com\/api\/media\//, '/api/media/');
    }
    if (clean.startsWith('http://') || clean.startsWith('https://')) {
      return clean;
    }
    const base = SERVER_URL.replace(/\/$/, '');
    return clean.startsWith('/') ? `${base}${clean}` : `${base}/${clean}`;
  };
  const maleVideoUrl = toAbsolute(exercise.videoUrlMale || exercise.videoUrl || '');
  const femaleVideoUrl = toAbsolute(exercise.videoUrlFemale || exercise.videoUrlMale || exercise.videoUrl || '');

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{exercise._id ? 'Edit Exercise' : 'Add Exercise'}</Typography>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
              <TextField
                label="Exercise Name *"
                value={exercise.name}
                onChange={e => update('name', e.target.value)}
                fullWidth
                helperText={canUseAI ? 'Type the name, then let AI draft trainer-quality fields.' : ''}
              />
              {canUseAI && (
                <Button
                  variant="outlined"
                  onClick={handleGenerateAI}
                  disabled={aiGenerating || !exercise.name?.trim()}
                  startIcon={aiGenerating ? <CircularProgress size={16} /> : <AutoAwesome sx={{ fontSize: 18 }} />}
                  sx={{
                    minWidth: { xs: '100%', sm: 148 },
                    height: 40,
                    mt: { xs: 0, sm: 1 },
                    whiteSpace: 'nowrap',
                    '& .MuiButton-startIcon': { mr: 0.75 },
                    '& .MuiSvgIcon-root': { width: 18, height: 18 },
                  }}
                >
                  {aiGenerating ? 'Generating...' : 'Use AI'}
                </Button>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Description *" value={exercise.description} onChange={e => update('description', e.target.value)} fullWidth multiline rows={3} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Muscle Group *" value={exercise.muscleGroup} onChange={e => update('muscleGroup', e.target.value)} fullWidth>
              {MUSCLE_GROUPS.map(mg => <MenuItem key={mg} value={mg}>{mg}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Difficulty" value={exercise.difficulty} onChange={e => update('difficulty', e.target.value)} fullWidth>
              {['beginner', 'intermediate', 'advanced'].map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Activity Type" value={exercise.activityType} onChange={e => update('activityType', e.target.value)} fullWidth>
              {ACTIVITY_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Duration (seconds)" type="number" value={exercise.durationSeconds} onChange={e => update('durationSeconds', e.target.value)} fullWidth inputProps={{ min: 0 }} helperText="For timed exercises (yoga, intervals)" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Seconds per Rep" type="number" value={exercise.secondsPerRep} onChange={e => update('secondsPerRep', e.target.value)} fullWidth inputProps={{ min: 1, step: 0.5 }} helperText="Timer interval during workout (default: 2 sec)" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Breath Count" type="number" value={exercise.breathCount || ''} onChange={e => update('breathCount', e.target.value)} fullWidth inputProps={{ min: 1 }} helperText="Yoga/pranayama: number of breaths to hold pose" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Calories Burned per Rep" type="number" value={exercise.caloriesBurnedPerRep} onChange={e => update('caloriesBurnedPerRep', e.target.value)} fullWidth inputProps={{ min: 0, step: 0.1 }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Coaching Cue" value={exercise.coachingCue || ''} onChange={e => update('coachingCue', e.target.value)} fullWidth helperText="Short tip shown on-screen during the exercise (e.g. 'Keep core tight, lower chest to 2 cm from floor')" />
          </Grid>

          {/* Image */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" fontWeight={600} mb={1}>Exercise Image</Typography>
            {exercise.imageUrl ? (
              <Box>
                <Box component="img" src={exercise.imageUrl} alt="preview"
                  sx={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider', display: 'block' }}
                  onError={e => { e.target.style.display = 'none'; }} />
                <Stack direction="row" spacing={1} mt={1}>
                  <Button size="small" variant="outlined" component="label"
                    startIcon={imageUploading ? <CircularProgress size={14} /> : <ImageIcon />} disabled={imageUploading}>
                    {imageUploading ? 'Uploading...' : 'Change'}
                    <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                  </Button>
                  <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutline />} onClick={() => update('imageUrl', '')}>Remove</Button>
                </Stack>
              </Box>
            ) : (
              <Button variant="outlined" component="label"
                startIcon={imageUploading ? <CircularProgress size={16} /> : <ImageIcon />}
                disabled={imageUploading} fullWidth sx={{ height: 56 }}>
                {imageUploading ? 'Uploading...' : 'Upload Image'}
                <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
              </Button>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField label="Equipment (comma separated)" value={equipmentValue} onChange={e => update('equipment', e.target.value)} fullWidth helperText="e.g. barbell, dumbbells, resistance band" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Instructions (one per line)" value={instructionsValue} onChange={e => update('instructions', e.target.value)} fullWidth multiline rows={4} helperText="Each line becomes a separate instruction step" />
          </Grid>

          {/* â”€â”€ Video / Media â”€â”€ */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>Video / Media</Typography>
            <Stack direction="row" spacing={1} mb={2}>
              {[
                { key: 'none', label: 'None' },
                { key: 'upload', label: 'Upload Video', icon: <VideoFile sx={{ fontSize: 16 }} /> },
                { key: 'youtube', label: 'YouTube Link', icon: <YouTube sx={{ fontSize: 16 }} /> },
              ].map(({ key, label, icon }) => (
                <Button key={key} size="small"
                  variant={mediaMode === key ? 'contained' : 'outlined'}
                  startIcon={icon}
                  onClick={() => handleMediaModeChange(key)}>
                  {label}
                </Button>
              ))}
            </Stack>

            {mediaMode === 'upload' && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <Alert severity="info" sx={{ mb: 2, fontSize: '0.8125rem' }}>
                  Upload separate videos for male and female. If only male is provided, it will be used for both.
                  Videos are stored under <strong>uploads/videos/{'{gym}'}/{'{muscleGroup}'}/{'{gender}'}/</strong>
                </Alert>
                <Grid container spacing={2}>
                  {/* Male video */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Male Video <Chip label="default" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                    </Typography>
                    {maleVideoUrl && !pendingMaleVideo ? (
                      <Box>
                        <Box component="video" src={maleVideoUrl} controls preload="metadata" crossOrigin="anonymous"
                          onError={(e) => {
                            if (exercise.videoUrl && e.target.src !== toAbsolute(exercise.videoUrl)) {
                              e.target.src = toAbsolute(exercise.videoUrl);
                            }
                          }}
                          sx={{ width: '100%', maxHeight: 140, borderRadius: 1, border: 1, borderColor: 'divider', display: 'block' }} />
                        <Stack direction="row" spacing={1} mt={1}>
                          <Button size="small" variant="outlined" component="label" startIcon={<VideoFile />}>
                            Replace
                            <input type="file" hidden accept="video/*" onChange={e => { setPendingMaleVideo(e.target.files[0] || null); }} />
                          </Button>
                          <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutline />}
                            onClick={() => { update('videoUrlMale', ''); update('videoUrl', ''); }}>Remove</Button>
                        </Stack>
                      </Box>
                    ) : (
                      <Box>
                        <Button variant="outlined" component="label" fullWidth sx={{ height: 56 }}
                          startIcon={<VideoFile />}>
                          {pendingMaleVideo ? pendingMaleVideo.name : 'Choose Male Video'}
                          <input type="file" hidden accept="video/*" onChange={e => { setPendingMaleVideo(e.target.files[0] || null); }} />
                        </Button>
                        {pendingMaleVideo && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Will upload on save
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Grid>

                  {/* Female video */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Female Video <Chip label="optional" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                    </Typography>
                    {femaleVideoUrl && femaleVideoUrl !== maleVideoUrl && !pendingFemaleVideo ? (
                      <Box>
                        <Box component="video" src={femaleVideoUrl} controls preload="metadata" crossOrigin="anonymous"
                          onError={(e) => {
                            if (exercise.videoUrl && e.target.src !== toAbsolute(exercise.videoUrl)) {
                              e.target.src = toAbsolute(exercise.videoUrl);
                            }
                          }}
                          sx={{ width: '100%', maxHeight: 140, borderRadius: 1, border: 1, borderColor: 'divider', display: 'block' }} />
                        <Stack direction="row" spacing={1} mt={1}>
                          <Button size="small" variant="outlined" component="label" startIcon={<VideoFile />}>
                            Replace
                            <input type="file" hidden accept="video/*" onChange={e => { setPendingFemaleVideo(e.target.files[0] || null); }} />
                          </Button>
                          <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutline />}
                            onClick={() => update('videoUrlFemale', '')}>Remove</Button>
                        </Stack>
                      </Box>
                    ) : (
                      <Box>
                        <Button variant="outlined" component="label" fullWidth sx={{ height: 56 }}
                          startIcon={<VideoFile />}>
                          {pendingFemaleVideo ? pendingFemaleVideo.name : 'Choose Female Video'}
                          <input type="file" hidden accept="video/*" onChange={e => { setPendingFemaleVideo(e.target.files[0] || null); }} />
                        </Button>
                        {!pendingFemaleVideo && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            If not provided, male video will be used for female users
                          </Typography>
                        )}
                        {pendingFemaleVideo && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Will upload on save
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}

            {mediaMode === 'youtube' && (
              <TextField
                label="YouTube Link"
                value={exercise.youtubeLink}
                onChange={e => update('youtubeLink', e.target.value)}
                fullWidth
                placeholder="https://www.youtube.com/watch?v=..."
                InputProps={{
                  startAdornment: <InputAdornment position="start"><YouTube color="error" /></InputAdornment>,
                }}
                helperText="Must start with https://www.youtube.com/ or https://youtu.be/"
              />
            )}
          </Grid>

          {isApprover && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={!!exercise.isPublished} onChange={e => update('isPublished', e.target.checked)} />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Published</Typography>
                    <Typography variant="caption" color="text.secondary">Published exercises are visible to users in the app</Typography>
                  </Box>
                }
              />
            </Grid>
          )}

          {isTrainer && (
            <Grid item xs={12}>
              <Alert severity="info" icon={false}>
                Your exercise will be saved as a draft. Use "Submit for Review" to send it to an admin for approval.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || videoUploading || imageUploading || aiGenerating}>
          {videoUploading ? 'Uploading videos...' : saving ? 'Saving...' : exercise._id ? 'Update Exercise' : 'Add Exercise'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
